import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { useEffect, useEffectEvent, useRef } from "react";
import type { ReactNode } from "react";

export interface InfiniteScrollSentinelProps {
  dataLength: number;
  hasMore?: boolean;
  loader?: ReactNode;
  onLoadMore: () => void;
  prefetchMargin?: string;
  scrollContainer?: HTMLElement | null;
}

/**
 * Renders a loader at the end of a list and calls `onLoadMore` as it scrolls
 * into view, loading more items as the user reaches the bottom. Renders nothing
 * once `hasMore` is false. Pass the current item count as `dataLength`.
 *
 * @example
 * ```tsx
 * const { items, fetchNextPage, hasNextPage } = useItems();
 *
 * return (
 *   <>
 *     {items.map(item => (
 *       <Item key={item.id} item={item} />
 *     ))}
 *     <InfiniteScrollSentinel
 *       dataLength={items.length}
 *       hasMore={hasNextPage}
 *       onLoadMore={fetchNextPage}
 *     />
 *   </>
 * );
 * ```
 */
export const InfiniteScrollSentinel = ({
  dataLength,
  hasMore = false,
  loader = <LoaderRow />,
  onLoadMore,
  prefetchMargin = "200px",
  scrollContainer = null,
}: InfiniteScrollSentinelProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMore = useEffectEvent(onLoadMore);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;

    let hasTriggered = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !hasTriggered) {
          hasTriggered = true;
          loadMore();
        }
      },
      { root: scrollContainer, rootMargin: prefetchMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [dataLength, hasMore, prefetchMargin, scrollContainer]);

  return hasMore ? <div ref={sentinelRef}>{loader}</div> : null;
};
